import React from "react";
import SEO from "../component/SEO";

const TermsConditions = () => {
  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <SEO title="Terms & Conditions - K Bakery" description="Read our Terms and Conditions for ordering and services." />
      <h1 className="text-4xl md:text-5xl font-bold text-pink-700 mb-6 text-center">
        Terms & Conditions – K Bakery
      </h1>

      <p className="text-gray-700 mb-6">
        <strong>K Bakery</strong> (hereinafter referred to as “the Bakery”) issues the following Terms & Conditions, which shall apply to all transactions, without limitation, between the Bakery and any individual or entity (hereinafter referred to as “the Customer”).
      </p>

      <p className="text-gray-700 mb-6">
        By placing an order, making a payment, or otherwise engaging with the Bakery, the Customer hereby expressly acknowledges and agrees to be bound by the following provisions:
      </p>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">1. DEFINITIONS</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>“Products” – all baked goods, desserts, cakes, and related items prepared and sold by the Bakery.</li>
          <li>“Order” – a request made by the Customer, confirmed by payment of a deposit, for the supply of Products by the Bakery.</li>
          <li>“Deposit” – a non-refundable partial payment required to confirm an Order.</li>
          <li>“Final Payment” – the balance owed by the Customer prior to release of the Products.</li>
          <li>“Collection” – the physical act of the Customer taking possession of the Products from the Bakery premises.</li>
          <li>“Delivery” – transportation of the Products by the Bakery (or its authorized representative) to the location specified by the Customer.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">2. GENERAL POLICY</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li><strong>Strict Egg-Free Operation:</strong> The Bakery is a 100% egg-free facility. No eggs or egg-derived substances will be incorporated under any circumstances.</li>
          <li><strong>Variability:</strong> Products are handmade; minor variations in design, size, decoration, or color are not defects.</li>
          <li><strong>Modification of Terms:</strong> The Bakery may update these Terms, pricing, or product offerings without prior notice.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">3. ORDERS & PAYMENTS</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>No Order is confirmed until the required Deposit is received.</li>
          <li>Deposits are strictly non-refundable and non-transferable.</li>
          <li>Final Payment must be made prior to Collection or Delivery. Failure to pay allows the Bakery to withhold Products.</li>
          <li>All payments are final; dissatisfaction with subjective elements (sweetness, flavor, design interpretation) does not entitle a refund.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">4. CANCELLATIONS & REFUNDS</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Cancellations  &gt; hours before Collection/Delivery may be considered for partial refund (excluding Deposit) at Bakery’s discretion.</li>
          <li>Cancellations ≤24 hours before Collection/Delivery result in forfeiture of all payments.</li>
          <li>The Bakery is not liable for delays or failures due to force majeure events (natural disasters, strikes, supplier shortages, etc.).</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">5. CUSTOMIZATION & DESIGN</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Bakery will make best efforts to honor customization requests, but exact replication is not guaranteed.</li>
          <li>Bakery retains creative discretion; reasonable artistic variation is not a breach of contract.</li>
          <li>Customer is responsible for accuracy of all communicated details (spellings, flavor, size).</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">6. ALLERGIES & DIETARY INFORMATION</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>All Products are egg-free. Premises handle nuts, dairy, gluten, soy, and other allergens. Cross-contact risk cannot be fully eliminated.</li>
          <li>Customers must disclose allergies or dietary restrictions. Bakery is not liable for adverse reactions once Products leave its control.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">7. COLLECTION & DELIVERY</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Customer must collect Products at the agreed time. Delays may affect quality; Bakery bears no responsibility.</li>
          <li>Risk transfers to Customer upon Collection or Delivery; Bakery is not liable thereafter.</li>
          <li>Delivery incurs extra charges; times are approximate; Bakery is not liable for delays from external conditions.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">8. STORAGE & HANDLING</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Products must be stored per Bakery instructions. Improper storage voids claims against Bakery for quality or safety.</li>
          <li>Issues from mishandling, transport, or environmental exposure after leaving Bakery are Customer’s responsibility.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">9. MARKETING & PHOTOGRAPHY</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Bakery may photograph Products for marketing, advertising, or portfolio purposes without notice or compensation.</li>
          <li>Customer identity remains confidential unless disclosure is legally required.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">10. LIMITATION OF LIABILITY</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Bakery is not liable for indirect, consequential, or incidental damages from use or consumption of Products.</li>
          <li>Bakery’s liability does not exceed the monetary value paid by the Customer for the specific Order in dispute.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">11. ENTIRE AGREEMENT</h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>These Terms constitute the entire agreement between Bakery and Customer.</li>
          <li>No oral statements or external communications override these provisions.</li>
          <li>Agreement governed by applicable law in the Bakery’s jurisdiction.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">12. CONTACT INFORMATION</h2>
        <p className="text-gray-700">
          All formal correspondence shall be directed to:
          <br />
          <strong>
            <a href="mailto:kbakery1002@gmail.com" className="text-pink-600">kbakery1002@gmail.com</a>
          </strong>
          {/* <br />
          <strong>
            <a href="mailto:contact@kbakeryy.com" className="text-pink-600">contact@kbakeryy.com</a>
          </strong> */}
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold text-pink-600 mb-3">FINAL ACKNOWLEDGMENT</h2>
        <p className="text-gray-700">
          By remitting payment or placing an Order, the Customer expressly acknowledges and agrees to be legally bound by these Terms & Conditions in their entirety.
        </p>
      </section>
    </div>
  );
};

export default TermsConditions;
